import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Icon } from '@/lib/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { pageTransition, pageTransitionConfig } from '@/lib/motion';
import { BackLink } from '@/components/ui/BackLink';
import { SeitenverlaufProvider } from '@/lib/SeitenverlaufProvider';
import { CommandPalette, CommandPaletteProvider } from '@/skills/smart-search';
import { BotBubble } from '@/skills/ai-smart-assistant';
import { SprachsteuerungPanel, SprachsteuerungProvider } from '@/skills/voice-control';

/**
 * Top-Level-Shell mit responsivem Layout:
 *  - md+ : Sidebar als feste Spalte (256 px, einklappbar auf 64 px).
 *  - sm  : Sidebar als Off-Canvas-Drawer, ein/aus per TopBar-Hamburger.
 *
 * Routenwechsel schließt das mobile Menü automatisch.
 */
export function AppShell() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Drawer-Lifecycle: Body-Scroll-Lock, ESC, Focus-Move & -Restore.
  // Restore-Target ist das Element, das beim Öffnen fokussiert war (meistens
  // der Hamburger-Button im TopBar) — so springt der Fokus nach dem Schließen
  // dorthin zurück, statt am <body> zu landen.
  useEffect(() => {
    if (!mobileMenuOpen) return;
    previousFocusRef.current = (document.activeElement as HTMLElement) ?? null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const frame = requestAnimationFrame(() => drawerRef.current?.focus());

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      previousFocusRef.current?.focus?.();
    };
  }, [mobileMenuOpen]);

  return (
    // Reihenfolge mit Absicht: Das Sprach-Bedienfeld kann die Schnellsuche
    // öffnen, sein Provider muss also innerhalb des Palette-Providers liegen.
    <SeitenverlaufProvider>
      <CommandPaletteProvider>
        <SprachsteuerungProvider>
          <div className="flex min-h-screen bg-ink-50">
            {/* Sidebar — auf md+ fest, auf mobil als Drawer */}
            <div className="hidden md:flex md:shrink-0">
              <Sidebar />
            </div>

            {/* Mobile Drawer + Overlay */}
            <AnimatePresence>
              {mobileMenuOpen ? (
                <>
                  <motion.div
                    key="overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="fixed inset-0 z-40 bg-navy-950/60 md:hidden"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-hidden
                  />
                  <motion.div
                    key="drawer"
                    ref={drawerRef}
                    tabIndex={-1}
                    initial={{ x: '-100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '-100%' }}
                    transition={{ type: 'spring', stiffness: 320, damping: 32 }}
                    className="fixed inset-y-0 left-0 z-50 outline-none md:hidden"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Hauptmenü"
                  >
                    {/* Kein Einklappen im Drawer — der Hamburger blendet ihn
                    ohnehin komplett aus, eine Icon-Leiste wäre doppelt. */}
                    <Sidebar collapsible={false} />
                  </motion.div>
                </>
              ) : null}
            </AnimatePresence>

            <div className="flex min-w-0 flex-1 flex-col">
              <TopBar
                mobileMenuOpen={mobileMenuOpen}
                onToggleMobileMenu={() => setMobileMenuOpen((o) => !o)}
              />
              <main className="flex-1 overflow-x-hidden">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={location.pathname}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={pageTransition}
                    transition={pageTransitionConfig}
                    className="px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6"
                  >
                    {/* Rückweg über der Seite statt in jeder einzelnen: die
                      Hülle weiß, wo der Nutzer war, die Seite nicht. */}
                    <BackLink className="mb-3" />
                    <Suspense
                      fallback={
                        <div className="flex h-[60vh] items-center justify-center text-ink-500">
                          <Icon name="Loader2" size={24} className="animate-spin text-accent-500" />
                        </div>
                      }
                    >
                      <Outlet />
                    </Suspense>
                  </motion.div>
                </AnimatePresence>
              </main>
            </div>
          </div>
          <CommandPalette />
          <BotBubble />
          {/* Rendert nichts, solange der Feature-Flag aus ist. */}
          <SprachsteuerungPanel />
        </SprachsteuerungProvider>
      </CommandPaletteProvider>
    </SeitenverlaufProvider>
  );
}
